export const VACCINE_NAMES = {
  BCG: "Bacillus Calmette Guerin (BCG)",
  OPV_0: "Oral Polio Vaccine(OPV) - 0",
  OPV: "Oral Polio Vaccine (OPV)",
  HEP_B_BIRTH: "Hepatitis B-Birth dose",
  PENTAVALENT: "Pentavalent",
  RVV: "Rotavirus(RVV)",
  FIPV: "Fractional dose of Inactivated Polio Vaccine(fIPV)",
  PCV: "Pneumococcal Conjugate Vaccine (PCV)",
  MR: "Measles and Rubella (MR)",
  JE: "Japanese Encephalitis(JE)",
  DPT_PRIMARY: "Diphtheria,Pertussis and Tetanus(DPT)",
  DPT_BOOSTER: "Diphtheria,Pertussis and Tetanus(DPT)-Booster",
  MMR: "Measles, Mumps and Rubella (MMR)",
  TYPHOID: "Typhoid",
  VITAMIN_A: "Vitamin - A",
  TD_PW: "Tetanus and Diphtheria(Td)",
  TD_BOOSTER: "Tetanus and Diphtheria(Td) Booster",
  TD_ADOLESCENT: "Tetanus and Diptheria (Td) - Adolescent",
};

export const normalizeVaccineName = (name) =>
  typeof name === "string" ? name.trim().toLowerCase() : "";

export const normalizeMaterialId = (value, decryptFunction) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return value;
  }

  const parsedPlain = parseInt(value, 10);
  if (!Number.isNaN(parsedPlain) && String(value) === String(parsedPlain)) {
    return parsedPlain;
  }

  if (!decryptFunction || typeof value !== "string") {
    return value;
  }

  try {
    const decrypted = decryptFunction(value);
    const parsedDecrypted = parseInt(decrypted, 10);
    return Number.isNaN(parsedDecrypted) ? decrypted : parsedDecrypted;
  } catch (error) {
    return null;
  }
};

export const getRecordMaterialName = (record) =>
  record?.name ?? record?.material_name ?? record?.material?.material_name ?? null;

export const getRecordMaterialId = (record, decryptFunction) => {
  const value =
    record?.id ??
    record?.material_id ??
    record?.materialId ??
    record?.material?.id ??
    record?.material?.material_id ??
    null;

  return normalizeMaterialId(value, decryptFunction);
};

export const createVaccineIdentityRegistry = (materials = [], decryptFunction) => {
  const byName = new Map();
  const byId = new Map();

  (materials || []).forEach((material) => {
    const name = getRecordMaterialName(material);
    const id = getRecordMaterialId(material, decryptFunction);

    if (!name || id === null || id === undefined) {
      return;
    }

    const entry = {
      id,
      name,
      normalizedName: normalizeVaccineName(name),
      material,
    };

    byName.set(entry.normalizedName, entry);
    byId.set(id, entry);
  });

  return {
    byName,
    byId,
    getByName: (name) => byName.get(normalizeVaccineName(name)) || null,
    getById: (id) => byId.get(id) || null,
  };
};

export const createVaccineIdentityRegistryFromVaccines = (
  vaccines = [],
  decryptFunction
) => {
  const materials = [];
  const seen = new Set();

  (vaccines || []).forEach((vaccine) => {
    const id = getRecordMaterialId(vaccine, decryptFunction);
    const material = vaccine?.material || vaccine;

    if (id === null || id === undefined || seen.has(id)) {
      return;
    }

    seen.add(id);
    materials.push(material);
  });

  return createVaccineIdentityRegistry(materials, decryptFunction);
};

export const matchesVaccineIdentity = (
  record,
  expectedMaterialName,
  registry,
  decryptFunction,
  options = {}
) => {
  const expected = registry?.getByName?.(expectedMaterialName) || null;
  const recordId = getRecordMaterialId(record, decryptFunction);
  const recordName = getRecordMaterialName(record);

  const idMatched =
    expected?.id !== null &&
    expected?.id !== undefined &&
    recordId !== null &&
    recordId !== undefined &&
    recordId === expected.id;
  const nameMatched =
    normalizeVaccineName(recordName) === normalizeVaccineName(expectedMaterialName);

  if (
    options.warnOnMismatch &&
    expected &&
    recordId !== null &&
    recordId !== undefined &&
    recordName &&
    idMatched !== nameMatched
  ) {
    const mismatchPayload = {
      expectedMaterialName,
      expectedMaterialId: expected.id,
      recordMaterialName: recordName,
      recordMaterialId: recordId,
      idMatched,
      nameMatched,
      source: options.source || record?.source || null,
      record,
    };

    if (options.onMismatch) {
      options.onMismatch(mismatchPayload);
    } else {
      console.warn("[VACCINE_IDENTITY_MISMATCH]", mismatchPayload);
    }
  }

  return idMatched || nameMatched;
};

/**
 * Match a record to a canonical material name using the registry when available.
 * Falls back to normalized name comparison when registry is missing.
 */
export const recordMatchesMaterial = (
  record,
  canonicalMaterialName,
  decryptFunction,
  registry = null,
  matchOptions = {}
) => {
  if (registry) {
    return matchesVaccineIdentity(
      record,
      canonicalMaterialName,
      registry,
      decryptFunction,
      matchOptions
    );
  }

  const recordName = getRecordMaterialName(record);
  if (!canonicalMaterialName) {
    return false;
  }

  if (canonicalMaterialName === VACCINE_NAMES.PENTAVALENT) {
    return recordName?.includes("Pentavalent") ?? false;
  }

  return normalizeVaccineName(recordName) === normalizeVaccineName(canonicalMaterialName);
};

export const createVaccineIdentityHelpers = (registry, decryptFunction, options = {}) => ({
  isBcg: (record) => matchesVaccineIdentity(record, VACCINE_NAMES.BCG, registry, decryptFunction, options),
  isOpv0: (record) => matchesVaccineIdentity(record, VACCINE_NAMES.OPV_0, registry, decryptFunction, options),
  isOpv: (record) => matchesVaccineIdentity(record, VACCINE_NAMES.OPV, registry, decryptFunction, options),
  isHepBBirth: (record) => matchesVaccineIdentity(record, VACCINE_NAMES.HEP_B_BIRTH, registry, decryptFunction, options),
  isPentavalent: (record) => matchesVaccineIdentity(record, VACCINE_NAMES.PENTAVALENT, registry, decryptFunction, options),
  isRvv: (record) => matchesVaccineIdentity(record, VACCINE_NAMES.RVV, registry, decryptFunction, options),
  isFipv: (record) => matchesVaccineIdentity(record, VACCINE_NAMES.FIPV, registry, decryptFunction, options),
  isPcv: (record) => matchesVaccineIdentity(record, VACCINE_NAMES.PCV, registry, decryptFunction, options),
  isMr: (record) => matchesVaccineIdentity(record, VACCINE_NAMES.MR, registry, decryptFunction, options),
  isJe: (record) => matchesVaccineIdentity(record, VACCINE_NAMES.JE, registry, decryptFunction, options),
  isDptPrimary: (record) => matchesVaccineIdentity(record, VACCINE_NAMES.DPT_PRIMARY, registry, decryptFunction, options),
  isDptBooster: (record) => matchesVaccineIdentity(record, VACCINE_NAMES.DPT_BOOSTER, registry, decryptFunction, options),
  isMmr: (record) => matchesVaccineIdentity(record, VACCINE_NAMES.MMR, registry, decryptFunction, options),
  isTyphoid: (record) => matchesVaccineIdentity(record, VACCINE_NAMES.TYPHOID, registry, decryptFunction, options),
  isVitaminA: (record) => matchesVaccineIdentity(record, VACCINE_NAMES.VITAMIN_A, registry, decryptFunction, options),
  isTdPw: (record) => matchesVaccineIdentity(record, VACCINE_NAMES.TD_PW, registry, decryptFunction, options),
  isTdBooster: (record) => matchesVaccineIdentity(record, VACCINE_NAMES.TD_BOOSTER, registry, decryptFunction, options),
  isTdAdolescent: (record) => matchesVaccineIdentity(record, VACCINE_NAMES.TD_ADOLESCENT, registry, decryptFunction, options),
});
